// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { InvoicesDataViewContext } from "@insite/client-framework/Store/Data/Invoices/InvoicesSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { ListItem, UnorderedList } from "@insite/mobius/Lists";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Modal, { ModalPresentationProps } from "@store/MobiusOverrides/Modal/Modal";
import Typography, { TypographyPresentationProps } from "@store/MobiusOverrides/Typography/Typography";
import { getDocumentsWrapper } from "@store/Framework/Common/Utilities/getDocumentsWrapper";
import useTrackExportHistory from "@store/Framework/GoogleAnalytics/hooks/useTrackExportHistory";
import { getInvoiceHistory } from "@store/Framework/Services/documentService";
import { useGreifInvoiceExportSettings } from "@store/Framework/Store/Context/ContextHooks";
import { useInvoiceHistoryState } from "@store/Framework/Store/Greif/Pages/InvoiceHistory/InvoiceHistoryHooks";
import { tableFilterProps } from "@store/Themes/common/props/table";
import usePersistentDataView from "@toolkit/Framework/Common/Hooks/usePersistentDataView";
import React, { FC, useContext, useMemo, useState } from "react";

const dateDiffInDays = (a: Date, b: Date) => {
    const _MS_PER_DAY = 1000 * 60 * 60 * 24;
    // Discard the time and time-zone information.
    const utc1 = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
    const utc2 = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());

    return Math.floor((utc2 - utc1) / _MS_PER_DAY);
};

const checkEnforcementAndValueValidity = (value: number, allowedMaxValue: number, greater?: boolean) => {
    if (!!value || value > 0) {
        return greater ? value >= allowedMaxValue : value <= allowedMaxValue;
    }

    return false;
};

export interface InvoiceHistoryExportButtonProps extends WidgetProps {}

export interface InvoiceHistoryExportButtonStyles {
    conditionWarning?: TypographyPresentationProps;
    link?: LinkPresentationProps;
    loadingLink?: LinkPresentationProps;
    modal?: ModalPresentationProps;
    tooltip?: TooltipPresentationProps;
    wrapper?: InjectableCss;
}

export const invoiceHistoryExportButtonStyles: InvoiceHistoryExportButtonStyles = {
    link: tableFilterProps.exportLink,
    loadingLink: tableFilterProps.exportLoadingLink,
    modal: tableFilterProps.exportModal,
    tooltip: tableFilterProps.exportTooltip,
    conditionWarning: tableFilterProps.exportModalText,
};

const styles = invoiceHistoryExportButtonStyles;

const InvoiceHistoryExportButton: FC<InvoiceHistoryExportButtonProps> = () => {
    const { fromDate, toDate, fromDueDate, toDueDate } = useInvoiceHistoryState().parameter;
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    const fromInvoiceDateCompare = useMemo(() => new Date(fromDate!), [fromDate]);
    const toInvoiceDateCompare = useMemo(() => new Date(toDate || new Date()), [toDate]);
    const fromDueDateCompare = useMemo(() => new Date(fromDueDate!), [fromDueDate]);
    const toDueDateCompare = useMemo(() => new Date(toDueDate || new Date()), [toDueDate]);

    const toasterContext = useContext(ToasterContext);
    const invoicesDataView = usePersistentDataView(useContext(InvoicesDataViewContext));
    const trackExportHistory = useTrackExportHistory();

    const {
        enforceMaxNumberOfImports,
        enforceMaxDateRangeOfImports,
        maxNumberOfImports = 50,
        maxDateRangeOfImports = 90,
    } = useGreifInvoiceExportSettings() ?? {};

    let totalItemCount = 0;

    if (invoicesDataView.value && invoicesDataView.pagination) {
        totalItemCount = invoicesDataView.pagination.totalItemCount;
    }

    const exportInvoiceHistory = () => {
        const isValidDateRanges = () => {
            const numberOfInvoiceDaysFiltered = dateDiffInDays(fromInvoiceDateCompare!, toInvoiceDateCompare!);
            const numberOfDeliveryDaysFiltered = dateDiffInDays(fromDueDateCompare!, toDueDateCompare!);

            if (numberOfInvoiceDaysFiltered && !numberOfDeliveryDaysFiltered) {
                return checkEnforcementAndValueValidity(numberOfInvoiceDaysFiltered, maxDateRangeOfImports);
            }

            if (numberOfDeliveryDaysFiltered && !numberOfInvoiceDaysFiltered) {
                return checkEnforcementAndValueValidity(numberOfDeliveryDaysFiltered, maxDateRangeOfImports);
            }

            if (numberOfInvoiceDaysFiltered && numberOfDeliveryDaysFiltered) {
                const greaterDateFrom =
                    fromInvoiceDateCompare! < fromDueDateCompare! ? fromInvoiceDateCompare : fromDueDateCompare;

                const greaterDateTo =
                    toInvoiceDateCompare! < toDueDateCompare! ? toInvoiceDateCompare : toDueDateCompare;

                const numberOfCombinedDaysFilters = dateDiffInDays(greaterDateFrom!, greaterDateTo!);

                return checkEnforcementAndValueValidity(numberOfCombinedDaysFilters, maxDateRangeOfImports);
            }

            return false;
        };

        const canExport = () => {
            const isValidNumberOfInvoices = checkEnforcementAndValueValidity(totalItemCount, maxNumberOfImports);

            if (enforceMaxNumberOfImports && enforceMaxDateRangeOfImports) {
                return isValidNumberOfInvoices && isValidDateRanges();
            }

            if (enforceMaxNumberOfImports && !enforceMaxDateRangeOfImports) {
                return isValidNumberOfInvoices;
            }

            if (!enforceMaxNumberOfImports && enforceMaxDateRangeOfImports) {
                return isValidDateRanges();
            }

            return false;
        };

        if ((!enforceMaxNumberOfImports && !enforceMaxDateRangeOfImports) || canExport()) {
            getDocumentsWrapper(
                getInvoiceHistory,
                { parameter: window.location.search },
                { stateSetter: setIsExporting, toasterContext },
            ).then(e => {
                if (e) {
                    trackExportHistory({ file_category: "InvoiceHistory export" });
                }
            });
        } else {
            setIsModalOpen(true);
        }
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <Link
                {...(isExporting ? styles.loadingLink : styles.link)}
                data-test-selector="invoiceHistory_exportFilters"
                disabled={isExporting || invoicesDataView.isLoading || totalItemCount === 0}
                onClick={exportInvoiceHistory}
            >
                {translate("Export Invoice History")}
            </Link>
            <Tooltip
                {...styles.tooltip}
                text={translate(
                    "Exports invoices based on your current filters. Remove filters to export all invoices.",
                )}
            />
            <Modal
                {...styles.modal}
                headline={translate("Export Invoice History")}
                isOpen={isModalOpen}
                handleClose={() => setIsModalOpen(false)}
                data-test-selector="invoiceHistory_ExportModal"
            >
                <Typography {...styles.conditionWarning}>
                    {enforceMaxNumberOfImports && enforceMaxDateRangeOfImports
                        ? translate("Please ensure that the conditions below are respected.")
                        : translate("Please ensure that the condition below is respected.")}
                </Typography>
                <UnorderedList>
                    {enforceMaxNumberOfImports && (
                        <ListItem>
                            {siteMessage("InvoiceHistory_CantExport_NumberInvoices", String(maxNumberOfImports))}
                        </ListItem>
                    )}
                    {enforceMaxDateRangeOfImports && (
                        <ListItem>
                            {siteMessage("InvoiceHistory_CantExport_DateRangeDays", String(maxDateRangeOfImports))}
                        </ListItem>
                    )}
                </UnorderedList>
            </Modal>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: InvoiceHistoryExportButton,
    definition: {
        group: "Invoice History",
        displayName: "Export History Button",
        allowedContexts: [],
    },
};

export default widgetModule;
